import React, { useEffect, useMemo, useState } from 'react';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CardContent,
  CardActions,
  EyeIcon,
  EyeSlashIcon,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  SuccessCircleIcon,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { AvailityHeaderLogo, Footer, HIPAABanner, MessageDisplay, NewLoginBanner, NonProdBanner } from '@/components';
import { useAppContext } from '@/context';
import { useFRCallback } from '@/hooks';

const currentEnv = getSpecificEnv();
const shouldShowNonProdBanner = currentEnv !== 'prod';

const LoginStart = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const { queryParams, setQueryParams, setSessionCredentials, setMessages, addMessage } = useAppContext();
  const { callbacks = [], textOutputCallback = {} } = useFRCallback(stepToRender);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userId: '',
      password: '',
    },
  });

  const { errorCode: queryErrorCode } = queryParams;

  const loginBannerObj = useMemo(() => {
    let result;
    const { output = [] } = textOutputCallback;
    const bannerMsgObj = find(output, (outputObj) => outputObj?.name?.toLowerCase() === 'message');

    if (bannerMsgObj) {
      const parsedBannerMsg = JSON.parse(bannerMsgObj?.value);

      result = parsedBannerMsg;
    }

    return result;
  }, [textOutputCallback]);

  const bannerTitle = useMemo(() => loginBannerObj?.bannertitle, [loginBannerObj]);
  const bannerBody = useMemo(() => loginBannerObj?.bannertext, [loginBannerObj]);
  const bannerColor = useMemo(() => loginBannerObj?.bannercolor, [loginBannerObj]);

  useEffect(() => {
    if (queryErrorCode && ['102', '111'].indexOf(queryErrorCode) >= 0) {
      addMessage('You have been logged out. Please try again.');
    }

    setQueryParams({
      ...queryParams,
      errorCode: undefined,
    });
  }, [queryErrorCode]);

  const onSubmit = (values) => {
    setMessages([]); // clear out error messages on submit
    const output = { ...stepToRender };

    // TODO: flesh out proper logic
    if (values) {
      const { userId, password } = values;

      // set userId in queryParams context for later use
      setQueryParams({
        ...queryParams,
        userId,
      });

      const newCallbacks = [...callbacks];
      for (const [index, callbackField] of newCallbacks.entries()) {
        const { type } = callbackField;

        switch (type) {
          case 'NameCallback': {
            newCallbacks[index].input = [
              {
                ...newCallbacks[index].input[0],
                value: userId,
              },
            ];
            break;
          }
          case 'PasswordCallback': {
            newCallbacks[index].input = [
              {
                ...newCallbacks[index].input[0],
                value: password,
              },
            ];
            break;
          }
          default:
        }
      }

      Object.assign(output.payload.callbacks, [...newCallbacks]);

      // capture the user id into context
      setSessionCredentials({
        userId,
        password,
      });

      setCurrentStep(output);
    }
  };

  return (
    <Grid
      id="login-start-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <Box mt={2}>
          {shouldShowNonProdBanner && (
            <Grid mb={2} mx={2}>
              <NonProdBanner />
            </Grid>
          )}
          {bannerTitle && bannerBody && (
            <Grid mx={2}>
              <NewLoginBanner title={bannerTitle} body={bannerBody} color={bannerColor} />
            </Grid>
          )}
        </Box>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ maxWidth: '460px', marginX: 2 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent sx={{ paddingBottom: 1 }}>
                <Typography variant="h1" mb={2}>
                  Sign In
                </Typography>
                <Box>
                  <TextField
                    id="userId"
                    label="User ID"
                    margin="normal"
                    fullWidth
                    {...register('userId', {
                      required: 'Enter a valid user ID.',
                    })}
                    placeholder="Enter your user ID."
                    slotProps={{
                      htmlInput: {
                        'aria-required': true,
                      },
                    }}
                    error={!!errors.userId}
                    helperText={errors.userId?.message}
                  />
                  <TextField
                    id="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            title="password visibility"
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <EyeIcon fontSize="small" /> : <EyeSlashIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register('password', {
                      required: 'Enter a valid password.',
                    })}
                    placeholder="Enter your password."
                    slotProps={{
                      htmlInput: {
                        'aria-required': true,
                      },
                    }}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                </Box>
                <MessageDisplay />
                <Box>
                  <Button type="submit" fullWidth>
                    Sign In
                  </Button>
                </Box>
              </CardContent>
              <CardActions sx={{ borderTop: 0 }}>
                <Box
                  display="flex"
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Link
                    color="tertiary"
                    href="#/forgot-userid"
                    loadApp={false}
                    onClick={() => {
                      setMessages([]); // clear out messages on route navigation
                    }}
                  >
                    Forgot your user ID?
                  </Link>
                  <Link
                    color="tertiary"
                    href="#/forgot-password"
                    loadApp={false}
                    onClick={() => {
                      setMessages([]); // clear out messages on route navigation
                    }}
                    textAlign="right"
                  >
                    Forgot your password?
                  </Link>
                </Box>
              </CardActions>
            </form>
          </Paper>
        </Grid>
        <Grid sx={{ marginTop: 2, marginX: 2 }}>
          <HIPAABanner />
        </Grid>
        <Footer />
      </Grid>
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexGrow={1}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}
      >
        <Grid
          sx={{
            maxWidth: '500px',
            margin: 2,
          }}
          justifyContent="center"
        >
          <Typography variant="h1" mb={2}>
            New to Availity?
          </Typography>
          <Typography variant="h2" mb={2}>
            Create a free account and discover all the benefits of using Availity.
          </Typography>
          <List
            component="ul"
            sx={{
              listStylePosition: 'outside',
              marginBottom: 3,
            }}
            disablePadding
          >
            <ListItem disableGutters divider={false}>
              <ListItemIcon>
                <SuccessCircleIcon fontSize="medium" sx={{ color: '#ff651a', borderColor: '#ff651a' }} />
              </ListItemIcon>
              <ListItemText primary="Free, real-time access to hundreds of payers" />
            </ListItem>
            <ListItem disableGutters divider={false}>
              <ListItemIcon>
                <SuccessCircleIcon fontSize="medium" sx={{ color: '#ff651a', borderColor: '#ff651a' }} />
              </ListItemIcon>
              <ListItemText primary="Check eligibility, submit claims, collect patient payments and track ERAs" />
            </ListItem>
            <ListItem disableGutters divider={false}>
              <ListItemIcon>
                <SuccessCircleIcon fontSize="medium" sx={{ color: '#ff651a', borderColor: '#ff651a' }} />
              </ListItemIcon>
              <ListItemText primary="Update your provider profiles" />
            </ListItem>
            <ListItem disableGutters divider={false}>
              <ListItemIcon>
                <SuccessCircleIcon fontSize="medium" sx={{ color: '#ff651a', borderColor: '#ff651a' }} />
              </ListItemIcon>
              <ListItemText primary="Manage quality-of-care paperwork" />
            </ListItem>
          </List>
          <Grid display="flex">
            <Button fullWidth color="tertiary" onClick={() => navigate('/sign-up')}>
              Create a Free Account
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

LoginStart.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default LoginStart;
